
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import UtilitySelect from "@/components/UtilitySelect.vue";
import ConfirmChangeRequestDialog from "@/components/ConfirmChangeRequestDialog.vue";
import { PassengerChangeRequestStatus } from "@/store/passenger-store";

@Component({
  name: "PassengerEdit",
  components: { UtilitySelect, ConfirmChangeRequestDialog }
})
export default class PassengerEdit extends Vue {
  @Prop({ required: true })
  passenger!: IPassenger;

  mounted() {
    this.$nextTick(() => {
      (this.$refs.headingRef as HTMLElement)?.focus();
    });
  }

  @Watch("passenger", { immediate: true })
  passengerChanged(newVal: IPassenger) {
    if (newVal) {
      this.editedPassenger = JSON.parse(JSON.stringify(newVal));
    }
  }

  passengerFormValid: boolean = true;
  passengerInitalRules = [(v: string) => this.intialLengthRule(v)];
  editedPassenger: any = null;
  colors = [
    "#D32F2F",
    "#C2185B",
    "#7B1FA2",
    "#512DA8",
    "#303F9F",
    "#1976D2",
    "#0288D1",
    "#0097A7",
    "#00796B",
    "#388E3C",
    "#689F38",
    "#AFB42B",
    "#FBC02D",
    "#FFA000",
    "#F57C00",
    "#E64A19",
    "#5D4037",
    "#616161",
    "#455A64",
  ];

  dialog = false;
  token: string = "";
  confirmDialog = false;
  cancelConfirmDialog = false;
  saveError = false;
  saving = false;
  cancelingChangeRequest = false;
  utilityChanges: { added: number[]; removed: number[] } = { added: [], removed: [] };

  get passengers(): IPassenger[] {
    return this.$store.getters["passenger/passengers"];
  }

  get availableUtilities() {
    return this.$store.getters["passenger/availableUtilitiesFor"](this.passenger);
  }

  get pendingChangeRequest(): IPassengerChangeRequest | undefined {
    if (!this.passenger.changeRequests) return undefined;
    return this.passenger.changeRequests.find(c => c.status === PassengerChangeRequestStatus.Pending);
  }

  get hasPendingChanges(): boolean {
    return !!this.pendingChangeRequest;
  }

  get pendingSaveRequest(): Partial<IPassengerChangeRequest> {
    return {
      addedUtilities: this.utilityChanges.added.join(","),
      removedUtilities: this.utilityChanges.removed.join(","),
    };
  }

  get canCancelChangeRequest(): boolean {
    return !!this.pendingChangeRequest?.canBeCancelled;
  }

  onUtilitiesChange(changes: { added: number[]; removed: number[] }) {
    this.utilityChanges = changes;
  }

  onCancelChangeRequest() {
    this.cancelConfirmDialog = true;
  }

  async doCancelChangeRequest() {
    this.cancelingChangeRequest = true;
    try {
      await this.$store.dispatch("passenger/cancelChangeRequest", {
        passengerChangeRequest: this.pendingChangeRequest,
      });
      this.closePassengerDialog(true, false);
    } catch (_) {
      this.saveError = true;
    } finally {
      this.cancelingChangeRequest = false;
    }
  }

  onSaveClick() {
    const { added, removed } = this.utilityChanges;
    if (added.length > 0 || removed.length > 0) {
      this.confirmDialog = true;
    } else {
      this.savePassenger();
    }
  }

  intialLengthRule(v: string) {
    return (
      (v && v.length > 0 && v.length < 3) ||
      this.$t("error.length_between_one_and_two")
    );
  }

  closePassengerDialog(saveSuccess: boolean, saveError: boolean) {
    this.$emit("close", { saveSuccess, saveError });
  }

  async savePassenger() {
    this.saving = true;
    this.saveError = false;
    this.confirmDialog = false;
    try {
      if (this.passenger.avatarText !== this.editedPassenger.avatarText) {
        await this.$store.dispatch(
          "passenger/updatePassenger",
          this.editedPassenger
        );
      }
      const { added, removed } = this.utilityChanges;
      if (this.availableUtilities.length > 0 && (added.length > 0 || removed.length > 0)) {
        const passengerChangeRequest: IPassengerChangeRequest = {
          passengerId: this.passenger.id,
          addedUtilities: added.join(","),
          removedUtilities: removed.join(","),
          status: PassengerChangeRequestStatus.Pending,
          canBeCancelled: false,
        };
        await this.$store.dispatch("passenger/createChangeRequest", passengerChangeRequest);
      }
      this.closePassengerDialog(true, false);
    } catch (_) {
      this.saveError = true;
    } finally {
      this.saving = false;
    }
  }
}
